import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    ArrowDownload20Regular,
    ChevronLeft20Regular,
    ChevronRight20Regular,
    DocumentBulletList24Filled,
    Dismiss20Regular
} from "@fluentui/react-icons";
import Modal from "../Modal";
import {
    getFilenameFromContentDisposition,
    getPreviewKindFromContentTypeOrFilename,
    PreviewKind,
} from "../../utils/httpFileMeta";

export interface GalleryFileReference {
    uuid: string;
    fileName?: string;
}

interface OiFileGalleryModalProps {
    isOpen: boolean;
    onClose: () => void;
    files: GalleryFileReference[];
    initialIndex?: number;
    title?: string;
}

const URL_REGEX = /((https?:\/\/|www\.)[^\s<>"']+)/gi;

const splitTrailingPunctuation = (url: string): { url: string; trailing: string } => {
    let end = url.length;
    let trailing = "";

    const stripSimple = (ch: string) => ".,;:!?".includes(ch);
    const stripBracket = (ch: string) => "]}".includes(ch);

    while (end > 0) {
        const ch = url[end - 1];

        if (stripSimple(ch) || stripBracket(ch)) {
            trailing = ch + trailing;
            end -= 1;
            continue;
        }

        if (ch === ")") {
            const head = url.slice(0, end);
            const open = (head.match(/\(/g) || []).length;
            const close = (head.match(/\)/g) || []).length;
            if (close > open) {
                trailing = ch + trailing;
                end -= 1;
                continue;
            }
        }

        break;
    }

    return { url: url.slice(0, end), trailing };
};

const renderTextWithLinks = (text: string): React.ReactNode => {
    if (!text) return "";

    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;

    const regex = new RegExp(URL_REGEX.source, "gi");
    let match: RegExpExecArray | null = null;

    while ((match = regex.exec(text)) !== null) {
        const matchText = match[0];
        const matchIndex = typeof match.index === "number" ? match.index : -1;
        if (matchIndex < 0) continue;

        if (matchIndex > lastIndex) {
            nodes.push(text.slice(lastIndex, matchIndex));
        }

        const { url, trailing } = splitTrailingPunctuation(matchText);
        const href = url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;

        nodes.push(
            <a
                key={`url-${matchIndex}`}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 dark:text-blue-400 underline"
            >
                {url}
            </a>
        );

        if (trailing) {
            nodes.push(trailing);
        }

        lastIndex = matchIndex + matchText.length;
    }

    if (lastIndex < text.length) {
        nodes.push(text.slice(lastIndex));
    }

    return nodes;
};

const getFileUrl = (uuid: string) => `${process.env.REACT_APP_API_URL}/files/download/${uuid}`;

export default function OiFileGalleryModal({
    isOpen,
    onClose,
    files,
    initialIndex = 0,
    title = "Attachments"
}: OiFileGalleryModalProps) {
    const normalizedFiles = useMemo(() => {
        const uniqueByUuid = new Map<string, GalleryFileReference>();
        for (const file of files || []) {
            if (!file?.uuid) continue;
            uniqueByUuid.set(file.uuid, file);
        }
        return Array.from(uniqueByUuid.values());
    }, [files]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [textContent, setTextContent] = useState<string | null>(null);
    const [textLoading, setTextLoading] = useState(false);
    const [textError, setTextError] = useState<string | null>(null);
    const [tiffPngUrls, setTiffPngUrls] = useState<Record<string, string>>({});
    const [tiffLoading, setTiffLoading] = useState(false);
    const [tiffError, setTiffError] = useState<string | null>(null);

    const [heicPngUrls, setHeicPngUrls] = useState<Record<string, string>>({});
    const [heicLoading, setHeicLoading] = useState(false);
    const [heicError, setHeicError] = useState<string | null>(null);

    const [failedMedia, setFailedMedia] = useState<Record<string, boolean>>({});
    const tiffPngUrlsRef = useRef(tiffPngUrls);
    const heicPngUrlsRef = useRef(heicPngUrls);
    tiffPngUrlsRef.current = tiffPngUrls;
    heicPngUrlsRef.current = heicPngUrls;

    const [fileMetaByUuid, setFileMetaByUuid] = useState<
        Record<string, { contentType: string | null; fileName: string | null }>
    >({});
    const [metaLoadingUuid, setMetaLoadingUuid] = useState<string | null>(null);

    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
    const [fullscreenSrc, setFullscreenSrc] = useState<string | null>(null);
    const [fullscreenAlt, setFullscreenAlt] = useState<string>("");

    const filesScrollRef = useRef<HTMLDivElement | null>(null);
    const filesScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const filesScrollbarThumbRef = useRef<HTMLDivElement | null>(null);
    const filesDragStateRef = useRef<{ startY: number; startScrollTop: number } | null>(null);

    const [filesCanScroll, setFilesCanScroll] = useState(false);
    const [filesThumbHeight, setFilesThumbHeight] = useState(0);
    const [filesThumbTop, setFilesThumbTop] = useState(0);
    const [isFilesThumbDragging, setIsFilesThumbDragging] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        const safeIndex = Number.isFinite(initialIndex)
            ? Math.min(Math.max(initialIndex, 0), Math.max(normalizedFiles.length - 1, 0))
            : 0;
        setActiveIndex(safeIndex);
    }, [isOpen, initialIndex, normalizedFiles.length]);

    useEffect(() => {
        if (!isOpen) return;

        if (activeIndex > normalizedFiles.length - 1) {
            setActiveIndex(Math.max(normalizedFiles.length - 1, 0));
        }
    }, [activeIndex, isOpen, normalizedFiles.length]);

    const activeFile = normalizedFiles[activeIndex];
    const activeMeta = activeFile ? fileMetaByUuid[activeFile.uuid] : undefined;
    const activeFilenameForKind = activeMeta?.fileName ?? activeFile?.fileName ?? null;
    const activeKind: PreviewKind = activeFile
        ? getPreviewKindFromContentTypeOrFilename(activeMeta?.contentType, activeFilenameForKind)
        : "other";

    useEffect(() => {
        if (!isOpen) return;
        if (!activeFile?.uuid) return;
    }, [activeFile?.uuid, activeFilenameForKind, activeKind, activeMeta?.contentType, isOpen]);

    const getDisplayName = (file: GalleryFileReference | undefined): string => {
        if (!file) return "";
        return fileMetaByUuid[file.uuid]?.fileName ?? file.fileName ?? file.uuid;
    };

    const openFullscreen = (src: string, alt: string) => {
        setFullscreenSrc(src);
        setFullscreenAlt(alt);
        setIsFullscreenOpen(true);
    };

    const closeFullscreen = () => {
        setIsFullscreenOpen(false);
        setFullscreenSrc(null);
        setFullscreenAlt("");
    };

    const fullscreenOverlay =
        isFullscreenOpen && fullscreenSrc && typeof document !== "undefined"
            ? createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-primary-dark bg-opacity-90"
                    onClick={closeFullscreen}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Fullscreen preview"
                >
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            closeFullscreen();
                        }}
                        className="fixed top-4 right-4 p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-transparent border-none cursor-pointer text-primary-light"
                        aria-label="Close fullscreen preview"
                        title="Close"
                    >
                        <Dismiss20Regular className="w-5 h-5" />
                    </button>

                    <div className="w-screen h-screen p-4" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={fullscreenSrc}
                            alt={fullscreenAlt}
                            className="w-full h-full object-contain"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                </div>,
                document.body
            )
            : null;

    useEffect(() => {
        if (!isOpen) return;
        if (!activeFile?.uuid) return;
        if (fileMetaByUuid[activeFile.uuid]) return;

        const controller = new AbortController();
        const uuid = activeFile.uuid;

        const loadMeta = async () => {
            try {
                setMetaLoadingUuid(uuid);
                const res = await fetch(getFileUrl(uuid), { signal: controller.signal });
                if (!res.ok) {
                    throw new Error(`Failed to load file metadata (${res.status})`);
                }

                const contentType = res.headers.get("content-type");
                const contentDisposition = res.headers.get("content-disposition");
                const filename =
                    getFilenameFromContentDisposition(contentDisposition) ?? activeFile.fileName ?? null;

                // eslint-disable-next-line no-console
                console.debug("[OiFileGalleryModal] metadata content-type", {
                    uuid,
                    contentType,
                    fileName: filename,
                });

                setFileMetaByUuid((prev) => ({
                    ...prev,
                    [uuid]: { contentType, fileName: filename },
                }));

                try {
                    res.body?.cancel();
                } catch {
                    // ignore
                }
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") return;
                setFileMetaByUuid((prev) => ({
                    ...prev,
                    [uuid]: { contentType: null, fileName: activeFile.fileName ?? null },
                }));
            } finally {
                setMetaLoadingUuid((prev) => (prev === uuid ? null : prev));
            }
        };

        loadMeta();
        return () => controller.abort();
    }, [activeFile, fileMetaByUuid, isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setTextContent(null);
        setTextError(null);

        if (!activeFile) return;
        if (activeKind !== "text") return;

        const controller = new AbortController();
        const load = async () => {
            try {
                setTextLoading(true);
                const res = await fetch(getFileUrl(activeFile.uuid), { signal: controller.signal });
                if (!res.ok) {
                    throw new Error(`Failed to load text file (${res.status})`);
                }

                const contentType = res.headers.get("content-type");
                const contentDisposition = res.headers.get("content-disposition");
                const filename =
                    getFilenameFromContentDisposition(contentDisposition) ?? activeFile.fileName ?? null;
                setFileMetaByUuid((prev) =>
                    prev[activeFile.uuid]
                        ? prev
                        : {
                            ...prev,
                            [activeFile.uuid]: { contentType, fileName: filename },
                        }
                );

                const text = await res.text();
                setTextContent(text);
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") return;
                setTextError(e instanceof Error ? e.message : "Failed to load text file");
            } finally {
                setTextLoading(false);
            }
        };

        load();
        return () => controller.abort();
    }, [activeFile, activeFile?.uuid, activeKind, isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        setTiffError(null);
        setTiffLoading(false);

        if (!activeFile) return;
        if (activeKind !== "tiff") return;
        if (tiffPngUrls[activeFile.uuid]) return;

        const controller = new AbortController();
        const load = async () => {
            try {
                setTiffLoading(true);

                const res = await fetch(getFileUrl(activeFile.uuid), { signal: controller.signal });
                if (!res.ok) {
                    throw new Error(`Failed to load TIFF (${res.status})`);
                }

                const contentType = res.headers.get("content-type");
                const contentDisposition = res.headers.get("content-disposition");
                const filename =
                    getFilenameFromContentDisposition(contentDisposition) ?? activeFile.fileName ?? null;
                setFileMetaByUuid((prev) =>
                    prev[activeFile.uuid]
                        ? prev
                        : {
                            ...prev,
                            [activeFile.uuid]: { contentType, fileName: filename },
                        }
                );

                const buffer = await res.arrayBuffer();
                const UTIFMod: any = await import("utif");
                const UTIF = UTIFMod?.default ?? UTIFMod;

                const ifds = UTIF.decode(buffer);
                if (!ifds || ifds.length === 0) {
                    throw new Error("Invalid TIFF file");
                }

                UTIF.decodeImage(buffer, ifds[0]);
                const rgba = UTIF.toRGBA8(ifds[0]);
                const width = ifds[0].width as number;
                const height = ifds[0].height as number;

                if (!width || !height) {
                    throw new Error("Unsupported TIFF dimensions");
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    throw new Error("Canvas not supported");
                }

                const imageData = new ImageData(new Uint8ClampedArray(rgba), width, height);
                ctx.putImageData(imageData, 0, 0);

                const blob: Blob = await new Promise((resolve, reject) => {
                    canvas.toBlob((b) => {
                        if (!b) reject(new Error("Failed to convert TIFF"));
                        else resolve(b);
                    }, "image/png");
                });

                const url = URL.createObjectURL(blob);
                setTiffPngUrls((prev) => ({ ...prev, [activeFile.uuid]: url }));
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") return;
                setTiffError(e instanceof Error ? e.message : "Failed to preview TIFF");
            } finally {
                setTiffLoading(false);
            }
        };

        load();
        return () => controller.abort();
    }, [activeFile, activeFile?.uuid, activeKind, isOpen, tiffPngUrls]);

    useEffect(() => {
        if (!isOpen) return;

        setHeicError(null);
        setHeicLoading(false);

        if (!activeFile) return;
        if (activeKind !== "heic") return;
        if (heicPngUrls[activeFile.uuid]) return;

        const controller = new AbortController();
        const load = async () => {
            try {
                setHeicLoading(true);

                const res = await fetch(getFileUrl(activeFile.uuid), { signal: controller.signal });
                if (!res.ok) {
                    throw new Error(`Failed to load HEIC (${res.status})`);
                }

                const contentType = res.headers.get("content-type");
                const contentDisposition = res.headers.get("content-disposition");
                const filename =
                    getFilenameFromContentDisposition(contentDisposition) ?? activeFile.fileName ?? null;
                setFileMetaByUuid((prev) =>
                    prev[activeFile.uuid]
                        ? prev
                        : {
                            ...prev,
                            [activeFile.uuid]: { contentType, fileName: filename },
                        }
                );

                const buffer = await res.arrayBuffer();

                // Decode HEIC/HEIF in the browser via libheif (WASM bundle).
                const libheifMod: any = await import("libheif-js/wasm-bundle");
                const libheif = libheifMod?.default ?? libheifMod;
                if (!libheif?.HeifDecoder) {
                    throw new Error("HEIC decoder not available (libheif-js)");
                }

                const decoder = new libheif.HeifDecoder();
                const images = decoder.decode(new Uint8Array(buffer));
                if (!images || images.length === 0) {
                    throw new Error("Invalid HEIC/HEIF file");
                }

                const image = images[0];
                const width: number = image.get_width();
                const height: number = image.get_height();
                if (!width || !height) {
                    throw new Error("Unsupported HEIC/HEIF dimensions");
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    throw new Error("Canvas not supported");
                }

                const imageData = ctx.createImageData(width, height);
                await new Promise<void>((resolve, reject) => {
                    image.display(imageData, (displayData: any) => {
                        if (!displayData) {
                            reject(new Error("HEIF processing error"));
                            return;
                        }
                        resolve();
                    });
                });

                ctx.putImageData(imageData, 0, 0);

                const pngBlob: Blob = await new Promise((resolve, reject) => {
                    canvas.toBlob((b) => {
                        if (!b) reject(new Error("Failed to convert HEIC/HEIF"));
                        else resolve(b);
                    }, "image/png");
                });

                try {
                    if (typeof image?.free === "function") image.free();
                    if (typeof images?.dispose === "function") images.dispose();
                } catch {
                    // ignore cleanup errors
                }

                const url = URL.createObjectURL(pngBlob);
                setHeicPngUrls((prev) => ({ ...prev, [activeFile.uuid]: url }));
            } catch (e) {
                if (e instanceof Error && e.name === "AbortError") return;

                // eslint-disable-next-line no-console
                console.error("[OiFileGalleryModal] HEIC preview failed", {
                    uuid: activeFile?.uuid,
                    fileName: activeFile?.fileName,
                    contentType: activeMeta?.contentType ?? null,
                    error: e,
                });

                const message =
                    e instanceof Error
                        ? e.message
                        : typeof e === "string"
                            ? e
                            : (e as any)?.message
                                ? String((e as any).message)
                                : "Failed to preview HEIC";

                setHeicError(message);
            } finally {
                setHeicLoading(false);
            }
        };

        load();
        return () => controller.abort();
    }, [activeFile, activeFile?.uuid, activeKind, activeMeta?.contentType, heicPngUrls, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setFailedMedia(current => Object.keys(current).length > 0 ? {} : current);
        }
    }, [isOpen]);

    useEffect(() => () => {
        for (const url of [...Object.values(tiffPngUrlsRef.current), ...Object.values(heicPngUrlsRef.current)]) {
            try {
                URL.revokeObjectURL(url);
            } catch {
                // ignore
            }
        }
    }, []);

    useEffect(() => {
        if (isOpen) return;

        for (const url of Object.values(tiffPngUrls)) {
            try {
                URL.revokeObjectURL(url);
            } catch {
                // ignore
            }
        }

        if (Object.keys(tiffPngUrls).length > 0) {
            setTiffPngUrls({});
        }
    }, [isOpen, tiffPngUrls]);

    useEffect(() => {
        if (isOpen) return;

        for (const url of Object.values(heicPngUrls)) {
            try {
                URL.revokeObjectURL(url);
            } catch {
                // ignore
            }
        }

        if (Object.keys(heicPngUrls).length > 0) {
            setHeicPngUrls({});
        }
    }, [heicPngUrls, isOpen]);

    const canGoPrev = activeIndex > 0;
    const canGoNext = activeIndex < normalizedFiles.length - 1;

    const goPrev = () => setActiveIndex((i) => Math.max(i - 1, 0));
    const goNext = () => setActiveIndex((i) => Math.min(i + 1, normalizedFiles.length - 1));

    const updateFilesScrollbar = useCallback(() => {
        const scrollEl = filesScrollRef.current;
        const trackEl = filesScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;

        const scrollHeight = scrollEl.scrollHeight;
        const clientHeight = scrollEl.clientHeight;
        const scrollTop = scrollEl.scrollTop;
        const trackHeight = trackEl.clientHeight;

        const canScroll = scrollHeight > clientHeight + 1;
        setFilesCanScroll(canScroll);

        if (!canScroll || trackHeight <= 0) {
            setFilesThumbHeight(0);
            setFilesThumbTop(0);
            return;
        }

        const minThumbHeight = 24;
        const thumbHeight = Math.max(minThumbHeight, (clientHeight / scrollHeight) * trackHeight);
        const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
        const maxScrollTop = Math.max(0, scrollHeight - clientHeight);

        const thumbTop = maxScrollTop === 0 ? 0 : (scrollTop / maxScrollTop) * maxThumbTop;
        setFilesThumbHeight(thumbHeight);
        setFilesThumbTop(thumbTop);
    }, []);

    useLayoutEffect(() => {
        updateFilesScrollbar();
    }, [updateFilesScrollbar, normalizedFiles.length]);

    useEffect(() => {
        const scrollEl = filesScrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => updateFilesScrollbar();
        scrollEl.addEventListener("scroll", onScroll, { passive: true });

        let ro: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => updateFilesScrollbar());
            ro.observe(scrollEl);
            if (filesScrollbarTrackRef.current) ro.observe(filesScrollbarTrackRef.current);
        } else {
            window.addEventListener("resize", updateFilesScrollbar);
        }

        return () => {
            scrollEl.removeEventListener("scroll", onScroll);
            if (ro) ro.disconnect();
            else window.removeEventListener("resize", updateFilesScrollbar);
        };
    }, [updateFilesScrollbar]);

    useEffect(() => {
        if (!isFilesThumbDragging) return;

        const onPointerMove = (e: PointerEvent) => {
            const scrollEl = filesScrollRef.current;
            const trackEl = filesScrollbarTrackRef.current;
            const drag = filesDragStateRef.current;
            if (!scrollEl || !trackEl || !drag) return;

            const scrollHeight = scrollEl.scrollHeight;
            const clientHeight = scrollEl.clientHeight;
            const trackHeight = trackEl.clientHeight;
            const maxScrollTop = Math.max(0, scrollHeight - clientHeight);
            if (maxScrollTop === 0 || trackHeight <= 0) return;

            const maxThumbTop = Math.max(0, trackHeight - filesThumbHeight);
            if (maxThumbTop === 0) return;

            const deltaY = e.clientY - drag.startY;
            const scrollDelta = (deltaY / maxThumbTop) * maxScrollTop;
            scrollEl.scrollTop = drag.startScrollTop + scrollDelta;
        };

        const stopDragging = () => {
            setIsFilesThumbDragging(false);
            filesDragStateRef.current = null;
        };

        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", stopDragging);
        window.addEventListener("pointercancel", stopDragging);

        return () => {
            window.removeEventListener("pointermove", onPointerMove);
            window.removeEventListener("pointerup", stopDragging);
            window.removeEventListener("pointercancel", stopDragging);
        };
    }, [isFilesThumbDragging, filesThumbHeight]);

    const handleFilesThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        const scrollEl = filesScrollRef.current;
        if (!scrollEl) return;

        filesDragStateRef.current = { startY: e.clientY, startScrollTop: scrollEl.scrollTop };
        setIsFilesThumbDragging(true);
        filesScrollbarThumbRef.current?.setPointerCapture?.(e.pointerId);
        e.preventDefault();
    };

    const downloadFile = useCallback(
        async (file: GalleryFileReference) => {
            if (!file?.uuid) return;

            try {
                const res = await fetch(getFileUrl(file.uuid));
                if (!res.ok) {
                    throw new Error(`Failed to download file (${res.status})`);
                }

                const contentType = res.headers.get("content-type");
                const contentDisposition = res.headers.get("content-disposition");
                const filename =
                    getFilenameFromContentDisposition(contentDisposition) ??
                    fileMetaByUuid[file.uuid]?.fileName ??
                    file.fileName ??
                    file.uuid;

                setFileMetaByUuid((prev) => ({
                    ...prev,
                    [file.uuid]: { contentType, fileName: filename },
                }));

                const blob = await res.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[OiFileGalleryModal] download failed", {
                    uuid: file?.uuid,
                    fileName: file?.fileName,
                    error: e,
                });
            }
        },
        [fileMetaByUuid]
    );

    if (!isOpen) return null;

    const modal = (
        <Modal isOpen={isOpen} onClose={onClose} title={title} width="full" overlayClassName="z-[9998]">
            {fullscreenOverlay}

            {normalizedFiles.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">No attachments</div>
            ) : (
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <button
                            type="button"
                            onClick={goPrev}
                            disabled={!canGoPrev}
                            className="flex items-center gap-1 px-2 py-1 text-sm bg-oi-button-blue text-primary-light disabled:opacity-50"
                        >
                            <ChevronLeft20Regular />
                            Previous
                        </button>

                        <div className="flex items-center gap-2 min-w-0 max-w-[60%] text-primary-dark dark:text-primary-light">
                            <div className="text-sm font-medium truncate" title={getDisplayName(activeFile)}>
                                {getDisplayName(activeFile)}
                            </div>

                            <button
                                type="button"
                                disabled={!activeFile}
                                onClick={() => {
                                    if (!activeFile) return;
                                    void downloadFile(activeFile);
                                }}
                                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-transparent border-none cursor-pointer disabled:opacity-50"
                                title="Download original"
                                aria-label={activeFile ? `Download ${getDisplayName(activeFile)}` : "Download"}
                            >
                                <ArrowDownload20Regular className="w-4 h-4" />
                            </button>
                        </div>

                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!canGoNext}
                            className="flex items-center gap-1 px-2 py-1 text-sm bg-oi-button-blue text-primary-light disabled:opacity-50"
                        >
                            Next
                            <ChevronRight20Regular />
                        </button>
                    </div>

                    <div className="border border-collapse-header-light dark:border-collapse-header-dark rounded-md p-3 bg-white dark:bg-primary-dark">
                        {activeFile ? (
                            <div className="w-full h-[30vh] overflow-hidden flex items-center justify-center">
                                {activeKind === "image" && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        {failedMedia[activeFile.uuid] ? (
                                            <div className="text-sm text-bad-red">Failed to load image</div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="bg-transparent border-none p-0 cursor-zoom-in h-full w-full flex items-center justify-center"
                                                onClick={() => openFullscreen(getFileUrl(activeFile.uuid), getDisplayName(activeFile))}
                                                aria-label={`Open ${getDisplayName(activeFile)} fullscreen`}
                                            >
                                                <img
                                                    src={getFileUrl(activeFile.uuid)}
                                                    alt={getDisplayName(activeFile)}
                                                    className="h-full w-full object-contain"
                                                    decoding="async"
                                                    onError={() => setFailedMedia(current => ({ ...current, [activeFile.uuid]: true }))}
                                                />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {activeKind === "tiff" && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        {tiffLoading && (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Converting TIFF…</div>
                                        )}
                                        {tiffError && (
                                            <div className="text-sm text-bad-red">{tiffError}</div>
                                        )}
                                        {!tiffLoading && !tiffError && tiffPngUrls[activeFile.uuid] && (
                                            <button
                                                type="button"
                                                className="bg-transparent border-none p-0 cursor-zoom-in h-full w-full flex items-center justify-center"
                                                onClick={() =>
                                                    openFullscreen(tiffPngUrls[activeFile.uuid], getDisplayName(activeFile))
                                                }
                                                aria-label={`Open ${getDisplayName(activeFile)} fullscreen`}
                                            >
                                                <img
                                                    src={tiffPngUrls[activeFile.uuid]}
                                                    alt={getDisplayName(activeFile)}
                                                    className="h-full w-full object-contain"
                                                    loading="eager"
                                                    decoding="async"
                                                />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {activeKind === "heic" && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        {heicLoading && (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">Converting HEIC…</div>
                                        )}
                                        {heicError && (
                                            <div className="text-sm text-bad-red">{heicError}</div>
                                        )}
                                        {!heicLoading && !heicError && heicPngUrls[activeFile.uuid] && (
                                            <button
                                                type="button"
                                                className="bg-transparent border-none p-0 cursor-zoom-in h-full w-full flex items-center justify-center"
                                                onClick={() =>
                                                    openFullscreen(heicPngUrls[activeFile.uuid], getDisplayName(activeFile))
                                                }
                                                aria-label={`Open ${getDisplayName(activeFile)} fullscreen`}
                                            >
                                                <img
                                                    src={heicPngUrls[activeFile.uuid]}
                                                    alt={getDisplayName(activeFile)}
                                                    className="h-full w-full object-contain"
                                                    loading="eager"
                                                    decoding="async"
                                                />
                                            </button>
                                        )}
                                    </div>
                                )}

                                {activeKind === "video" && (
                                    <div className="w-full h-full flex items-center justify-center">
                                        {failedMedia[activeFile.uuid] ? (
                                            <div className="text-sm text-bad-red">Failed to load video</div>
                                        ) : (
                                            <video
                                                src={getFileUrl(activeFile.uuid)}
                                                controls
                                                className="h-full w-full object-contain"
                                                onError={() => setFailedMedia(current => ({ ...current, [activeFile.uuid]: true }))}
                                            />
                                        )}
                                    </div>
                                )}

                                {activeKind === "text" && (
                                    <div className="w-full h-full">
                                        {(textLoading || textError) && (
                                            <div className="w-full h-full flex items-center justify-center">
                                                {textLoading && (
                                                    <div className="text-sm text-gray-500 dark:text-gray-400">Loading text…</div>
                                                )}
                                                {textError && (
                                                    <div className="text-sm text-bad-red">{textError}</div>
                                                )}
                                            </div>
                                        )}

                                        {!textLoading && !textError && (
                                            <div className="w-full h-full overflow-auto">
                                                <pre className="text-xs whitespace-pre-wrap break-words text-primary-dark dark:text-primary-light">
                                                    {renderTextWithLinks(textContent ?? "")}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeKind === "other" && metaLoadingUuid === activeFile.uuid ? (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">Loading preview…</div>
                                ) : null}

                                {activeKind === "other" && metaLoadingUuid !== activeFile.uuid && (
                                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-sm text-primary-dark dark:text-primary-light text-center">
                                        <DocumentBulletList24Filled className="w-5 h-5" />
                                        <span>Preview not available for this file type</span>
                                        <button
                                            type="button"
                                            className="text-sm text-blue-600 dark:text-blue-400 underline bg-transparent border-none cursor-pointer"
                                            onClick={() => void downloadFile(activeFile)}
                                        >
                                            Download
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </div>

                    <div className="relative border border-collapse-header-light dark:border-collapse-header-dark rounded-md">
                        <div
                            ref={filesScrollRef}
                            className="h-32 overflow-y-auto pr-6"
                            onScroll={updateFilesScrollbar}
                        >
                            {normalizedFiles.map((file, idx) => (
                                <div
                                    key={file.uuid}
                                    className={`w-full flex items-center gap-2 p-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${idx === activeIndex ? "bg-gray-100 dark:bg-gray-700" : ""
                                        } h-8`}
                                    title={getDisplayName(file)}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setActiveIndex(idx)}
                                        className="flex min-w-0 flex-1 items-center gap-2 text-left bg-transparent border-none p-0"
                                    >
                                        <DocumentBulletList24Filled className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">{getDisplayName(file)}</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void downloadFile(file);
                                        }}
                                        className="ml-auto p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-primary-dark dark:text-primary-light bg-transparent border-none cursor-pointer"
                                        title="Download original"
                                        aria-label={`Download ${getDisplayName(file)}`}
                                    >
                                        <ArrowDownload20Regular className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className={`absolute top-1 bottom-1 right-1 ${filesCanScroll ? "" : "pointer-events-none opacity-0"}`}>
                            <div
                                ref={filesScrollbarTrackRef}
                                className="relative h-full w-3 rounded select-none bg-gray-100 dark:bg-gray-700"
                                aria-hidden={!filesCanScroll}
                            >
                                <div
                                    ref={filesScrollbarThumbRef}
                                    className={`bg-primary-dark dark:bg-primary-light absolute left-0 rounded w-3 ${isFilesThumbDragging ? "cursor-grabbing" : "cursor-grab"}`}
                                    style={{ height: `${filesThumbHeight}px`, transform: `translateY(${filesThumbTop}px)` }}
                                    onPointerDown={handleFilesThumbPointerDown}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Modal>
    );

    return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}
